import React, { useState, useEffect } from 'react';
import { Cookie, Check, X, FileText } from 'lucide-react';
import { createPortal } from 'react-dom';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // בדיקה אם המשתמש כבר נתן הסכמה
    const cookieConsent = localStorage.getItem('cookie_consent');
    if (!cookieConsent) {
      // הצגת הבאנר אחרי 2 שניות (כדי שהדף יטען)
      setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
      }, 2000);
    }
  }, []);

  const handleAccept = () => {
    setIsAnimating(false);
    setTimeout(() => {
      localStorage.setItem('cookie_consent', 'accepted');
      localStorage.setItem('cookie_consent_date', new Date().toISOString());
      localStorage.setItem('cookie_consent_version', '1.0');
      setIsVisible(false);
      
      // הוספת קוד Google Analytics או כלי ניתוח אחרים כאן
      console.log('🍪 Cookie consent granted - Analytics can now be loaded');
    }, 300);
  };

  const handleDecline = () => {
    setIsAnimating(false);
    setTimeout(() => {
      localStorage.setItem('cookie_consent', 'declined');
      localStorage.setItem('cookie_consent_date', new Date().toISOString());
      setIsVisible(false);
      
      console.log('🍪 Cookie consent declined - Only essential cookies will be used');
    }, 300);
  };

  if (!isVisible) return null;

  return createPortal(
    <div 
      className={`fixed bottom-0 left-0 right-0 z-[9999] transform transition-all duration-500 ${
        isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
      role="dialog"
      aria-label="הודעת עוגיות"
    >
      {/* רקע מטושטש */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>
      
      {/* התוכן */}
      <div className="relative bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 border-t-4 border-purple-400 shadow-2xl">
        {/* אפקטים מרקדים */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white/30 rounded-full animate-ping"
              style={{
                left: `${20 + (i * 15)}%`,
                top: `${20 + (i % 2) * 60}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: '2s'
              }}
            />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-4 lg:gap-6">
            {/* אייקון ומידע */}
            <div className="flex items-center gap-3 flex-grow text-center lg:text-right">
              <div className="hidden sm:block">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-400 to-amber-500 rounded-full flex items-center justify-center animate-bounce shadow-lg">
                  <Cookie className="w-6 h-6 text-white" />
                </div>
              </div>
              
              <div className="text-white">
                <h3 className="text-lg sm:text-xl font-bold mb-1 flex items-center justify-center lg:justify-start gap-2">
                  <Cookie className="w-5 h-5 sm:hidden" />
                  🍪 האתר עושה שימוש בעוגיות
                </h3>
                <p className="text-sm sm:text-base text-gray-200 leading-relaxed max-w-2xl">
                  לצורכי תפעול, אבטחה, ניתוח ושיפור חוויית המשתמש. 
                  המשך השימוש באתר מהווה הסכמה לשימוש בעוגיות בהתאם למדיניות העוגיות שלנו.
                </p>
              </div>
            </div>
            
            {/* כפתורים ופעולות */}
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 w-full lg:w-auto">
              {/* קישור למדיניות עוגיות */}
              <a
                href="/cookie-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 text-sm bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-200 border border-white/30 w-full sm:w-auto justify-center"
              >
                <FileText className="w-4 h-4" />
                <span>מדיניות עוגיות</span>
              </a>

              {/* כפתורי פעולה */}
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={handleDecline}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all duration-200 border border-gray-500 flex-1 sm:flex-initial"
                >
                  <X className="w-4 h-4" />
                  <span>רק חיוניות</span>
                </button>

                <button
                  onClick={handleAccept}
                  className="flex items-center justify-center gap-2 px-6 py-2 text-sm font-bold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex-1 sm:flex-initial"
                >
                  <Check className="w-4 h-4" />
                  <span>אני מסכים</span>
                </button>
              </div>
            </div>
          </div>

          {/* מידע נוסף */}
          <div className="mt-3 pt-3 border-t border-white/20">
            <p className="text-xs text-center text-gray-300">
              בלחיצה על "אני מסכים" תאפשרו לנו להשתמש בעוגיות לשיפור האתר. 
              ב"רק חיוניות" נשתמש רק בעוגיות הדרושות לתפקוד האתר.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CookieBanner;